import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';
import defaultImg from '../../assets/img/gl-1.png';
import { toast } from 'react-toastify';

interface Project {
  _id: string;
  name: string;
  city?: string;
  location?: string;
  status?: string;
  featuredImage?: string;
  slug?: string;
  propertyType?: string[];
  createdAt?: string;
}

export default function OwnerMyProperty() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchAll = async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const projData = await api.getMyProjects(token);
      if (Array.isArray(projData)) setProjects(projData);
    } catch (err) {
      console.error('Failed to fetch projects', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [token, user]);

  const handleDeleteProject = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    setDeleting(id);
    try {
      const res = await api.deleteProject(token!, id);
      if (res.message && res.message.toLowerCase().includes('success') || res.message === 'Project deleted') {
        setProjects(prev => prev.filter(p => p._id !== id));
        toast.success('Project deleted successfully');
      } else {
        toast.error(res.message || 'Failed to delete project');
      }
    } catch {
      toast.error('Error deleting project');
    }
    setDeleting(null);
  };

  return (
    <UserDashboardLayout>
      <div className="mb-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2 leading-none">My Properties</h1>
            <p className="text-slate-500 font-medium text-sm md:text-base">Manage your property portfolio, track views, and update listings.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/owner-add-property"
              className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#05144b] text-white hover:bg-[#0a1e66] text-[12px] md:text-[14px] font-bold rounded-md transition-all shadow-xl active:scale-95 no-underline"
            >
              <i className="fa-solid fa-plus-circle text-sm"></i> Add New Property
            </Link>
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-gray-100 dark:border-slate-800 shadow-sm">
            <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="font-bold text-[#05144b] text-[14px]">Loading your properties...</p>
          </div>
        ) : (
          <>
            {projects.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 rounded-md p-16 text-center border border-gray-100 dark:border-slate-800 shadow-sm">
                <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fa-solid fa-city text-3xl text-slate-300 dark:text-slate-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Properties Yet</h3>
                <p className="text-slate-500 mb-6 font-medium text-sm">Add your first property listing to get started.</p>
                <Link to="/owner-add-property" className="text-blue-600 font-bold text-sm hover:underline">
                  Add Your First Property &rarr;
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {projects.map(p => (
                  <div key={p._id} className="bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm p-5 flex flex-col md:flex-row items-start md:items-center gap-5 hover:shadow-lg transition-all group">
                    <div className="w-full md:w-32 h-24 rounded-md overflow-hidden shrink-0 bg-slate-50 border border-gray-100 dark:border-slate-800">
                      <img
                        src={p.featuredImage ? getImageUrl(p.featuredImage) : defaultImg}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        alt={p.name}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          p.status === 'Approved' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20' :
                          p.status === 'Rejected' ? 'bg-red-50 text-red-600 dark:bg-red-900/20' :
                          'bg-amber-50 text-amber-600 dark:bg-amber-900/20'
                        }`}>
                          {p.status || 'Pending'}
                        </span>
                        {Array.isArray(p.propertyType) && p.propertyType.length > 0 && (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-600 dark:bg-blue-900/20">
                            {p.propertyType[0]}
                          </span>
                        )}
                      </div>
                      <h4 className="text-lg font-bold text-slate-900 dark:text-white truncate">{p.name}</h4>
                      <p className="text-slate-500 text-xs font-medium flex items-center gap-1 mt-1">
                        <i className="fa-solid fa-location-dot"></i>
                        {[p.location, p.city].filter(Boolean).join(', ') || 'Location not set'}
                      </p>
                      {p.createdAt && (
                        <p className="text-slate-400 text-xs mt-1">
                          Added: {new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Link
                        to={`/project-details/${p.slug || p._id}`}
                        className="w-10 h-10 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-md flex items-center justify-center hover:bg-[#05144b] hover:text-white dark:hover:bg-slate-700 dark:hover:text-white transition-all border border-gray-100 dark:border-slate-700"
                        title="View"
                      >
                        <i className="fa-solid fa-eye text-sm"></i>
                      </Link>
                      <Link
                        to={`/owner-edit-project/${p._id}`}
                        className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-md flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all border border-blue-100 dark:border-blue-800/30"
                        title="Edit"
                      >
                        <i className="fa-solid fa-pen text-sm"></i>
                      </Link>
                      <button
                        onClick={() => handleDeleteProject(p._id)}
                        disabled={deleting === p._id}
                        className="w-10 h-10 bg-rose-50 dark:bg-rose-900/20 text-rose-600 rounded-md flex items-center justify-center hover:bg-rose-600 hover:text-white transition-all disabled:opacity-50 border border-rose-100 dark:border-rose-800/30"
                        title="Delete"
                      >
                        {deleting === p._id
                          ? <i className="fa-solid fa-circle-notch fa-spin text-sm"></i>
                          : <i className="fa-solid fa-trash-can text-sm"></i>}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </UserDashboardLayout>
  );
}
